import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Link,
  Skeleton,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { api } from '../../lib/api';

export function EmailPreferences() {
  const [marketing, setMarketing] = useState(false);
  const [adult, setAdult] = useState(false);
  const [saved, setSaved] = useState(false);
  const [hasChoice, setHasChoice] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/email-preferences');
      setMarketing(data.data.marketing);
      setSaved(data.data.marketing);
      setHasChoice(data.data.hasChoice);
      setAdult(data.data.adultConfirmed);
      setBlocked(data.data.outreachBlocked);
      setLoaded(true);
    } catch {
      setError('Email preferences could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  async function save(subscribe: boolean) {
    setLoading(true);
    setMessage('');
    setError('');
    try {
      await api.put('/email-preferences', { marketing: subscribe, adultConfirmed: adult });
      setMarketing(subscribe);
      setSaved(subscribe);
      setHasChoice(true);
      setBlocked(!subscribe);
      setMessage(
        subscribe
          ? 'You are subscribed to Paddiq emails.'
          : 'Marketing and administrator outreach are blocked. Essential account emails will continue.',
      );
    } catch {
      setError('Could not save your preference. Please try again.');
    } finally {
      setLoading(false);
    }
  }
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.2,
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row"
        spacing={1.25}
        alignItems="center"
        sx={{ px: 2, py: 1.7, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <MailOutlineRoundedIcon sx={{ fontSize: 19, color: 'text.secondary' }} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: '.88rem', fontWeight: 800 }}>Email preferences</Typography>
          <Typography sx={{ fontSize: '.75rem', color: 'text.secondary', mt: 0.2 }}>
            Choose what you hear from us.
          </Typography>
        </Box>
        {loaded && (
          <Typography sx={{ fontSize: '.68rem', fontWeight: 700, color: 'text.secondary' }}>
            {blocked
              ? 'Unsubscribed'
              : !hasChoice
                ? 'Enabled by default'
                : saved
                  ? 'Emails enabled'
                  : 'Emails off'}
          </Typography>
        )}
      </Stack>
      <Stack spacing={1.8} sx={{ p: 2 }}>
        {error && (
          <Alert
            severity="error"
            action={!loaded ? <Button onClick={() => void load()}>Retry</Button> : undefined}
          >
            {error}
          </Alert>
        )}
        {!loaded && loading ? (
          <Skeleton height={64} />
        ) : (
          <>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="flex-start"
            >
              <Box>
                <Typography
                  component="label"
                  htmlFor="marketing-email-switch"
                  sx={{ display: 'block', fontSize: '.8rem', fontWeight: 700 }}
                >
                  Product news and tips
                </Typography>
                <Typography
                  sx={{ fontSize: '.75rem', color: 'text.secondary', mt: 0.5, maxWidth: 440 }}
                >
                  Occasional updates, practical tips, and offers from Paddiq. Turn this off or use
                  the unsubscribe link in an email to opt out.
                </Typography>
              </Box>
              <Switch
                id="marketing-email-switch"
                size="small"
                checked={marketing}
                disabled={loading || !loaded}
                onChange={(e) => {
                  setMarketing(e.target.checked);
                  setMessage('');
                }}
                inputProps={{ 'aria-label': 'Product news and tips' }}
              />
            </Stack>
            {marketing && (!hasChoice || !saved) && (
              <Box sx={{ pl: 1.5, borderLeft: '2px solid', borderColor: 'primary.main' }}>
                <Typography sx={{ fontSize: '.74rem', color: 'text.secondary', mb: 0.5 }}>
                  I would like Paddiq to email me product news, tips, and offers. I can unsubscribe
                  at any time.
                </Typography>
                <FormControlLabel
                  sx={{
                    m: 0,
                    alignItems: 'center',
                    '& .MuiTypography-root': { fontSize: '.74rem' },
                  }}
                  control={
                    <Checkbox
                      size="small"
                      checked={adult}
                      disabled={loading}
                      onChange={(e) => setAdult(e.target.checked)}
                      sx={{ pl: 0, py: 0.5 }}
                    />
                  }
                  label="I am 18 or older and subscribing my own email address."
                />
              </Box>
            )}
            <Stack
              direction="row"
              spacing={1}
              sx={{ pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}
            >
              <LockOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled', mt: 0.15 }} />
              <Box>
                <Typography sx={{ fontSize: '.74rem', fontWeight: 600 }}>
                  Essential account emails
                </Typography>
                <Typography sx={{ fontSize: '.72rem', color: 'text.secondary', mt: 0.25 }}>
                  Password resets, verification, receipts, and security notices stay on.
                </Typography>
              </Box>
            </Stack>
            {message && (
              <Typography role="status" sx={{ fontSize: '.74rem', color: 'success.main' }}>
                {message}
              </Typography>
            )}
          </>
        )}
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        sx={{ px: 2, py: 1.25, borderTop: '1px solid', borderColor: 'divider' }}
      >
        <Stack direction="row" spacing={1.5}>
          <Link
            href="/privacy"
            target="_blank"
            rel="noreferrer"
            underline="hover"
            sx={{ fontSize: '.72rem', color: 'text.secondary' }}
          >
            Privacy policy
          </Link>
          {!blocked && loaded && (
            <Button
              size="small"
              variant="text"
              disabled={loading}
              onClick={() => void save(false)}
              sx={{
                minWidth: 0,
                p: 0,
                fontSize: '.72rem',
                textTransform: 'none',
                color: 'text.secondary',
              }}
            >
              Unsubscribe from outreach
            </Button>
          )}
        </Stack>
        <Button
          size="small"
          variant="contained"
          disableElevation
          disabled={
            !loaded || loading || (hasChoice && marketing === saved) || (marketing && !adult)
          }
          onClick={() => void save(marketing)}
          sx={{ textTransform: 'none', fontSize: '.74rem' }}
        >
          {loading && loaded ? 'Saving…' : 'Save preference'}
        </Button>
      </Stack>
    </Box>
  );
}
